import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Apple, Loader2, Smartphone, WalletCards } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '../ui/button';
import API_BASE from '../../utils/apiBase';

/**
 * Reusable Wallet section for the ONE canonical staff ID.
 *
 * It shows "Add to Apple Wallet" / "Add to Google Wallet" actions that reuse the
 * exact same staff identity (employee_code + verification QR) as the digital and
 * printed cards.  When a provider is not configured it renders a clear
 * "setup required" state and never performs a fake save/download.
 *
 * Props:
 *  - wallet:      the `wallet` block from the staff-ID payload ({ apple, google, same_staff_id })
 *  - authHeaders: Authorization headers for the API calls
 *  - endpoints:   optional { apple, google } paths (defaults to worker self-service)
 */
export default function StaffWalletButtons({ wallet, authHeaders, endpoints }) {
  const [busy, setBusy] = useState(null);
  const [failures, setFailures] = useState({});
  const contextRef = useRef(0);
  const operationRef = useRef(false);

  useEffect(() => {
    contextRef.current += 1;
    operationRef.current = false;
    setBusy(null);
    setFailures({});
    return () => {
      contextRef.current += 1;
      operationRef.current = false;
    };
  }, [wallet, authHeaders]);

  const paths = useMemo(
    () => ({
      apple: endpoints?.apple || '/worker/wallet/apple',
      google: endpoints?.google || '/worker/wallet/google',
    }),
    [endpoints],
  );

  const apple = failures.apple || wallet?.apple || { available: false, status: 'setup_required' };
  const google = failures.google || wallet?.google || { available: false, status: 'setup_required' };

  const describeSetup = (provider) => {
    if (provider?.status === 'setup_required') {
      return 'Your organisation is connecting Wallet. Your staff ID is available here in CareOps.';
    }
    if (provider?.status === 'pending_issue') {
      return 'Your staff ID is being issued — the Wallet pass will use the same identity.';
    }
    if (provider?.status === 'not_eligible') {
      return 'Available after recruitment approval, using the same staff ID.';
    }
    return provider?.reason || 'Your organisation is connecting Wallet. Your staff ID is available here in CareOps.';
  };

  const failed = (provider, error) => {
    const needsRefresh = error.response?.data?.detail?.code === 'not_issued';
    const message = needsRefresh
      ? 'Please refresh your staff ID before trying again.'
      : error.response?.status === 409
        ? 'Wallet is unavailable for this ID. Refresh your staff ID or ask your manager.'
        : `Could not create the ${provider === 'apple' ? 'Apple' : 'Google'} Wallet pass. Please try again.`;
    toast.error(message);
    if ([401, 403, 409].includes(error.response?.status)) {
      setFailures((previous) => ({ ...previous, [provider]: { available: false, status: 'unavailable', reason: message } }));
    }
  };

  const handleApple = async () => {
    if (!apple.available || operationRef.current) return;
    operationRef.current = true;
    const context = contextRef.current;
    setBusy('apple');
    try {
      const response = await axios.get(`${API_BASE}${paths.apple}`, {
        headers: authHeaders,
        responseType: 'blob',
      });
      if (context !== contextRef.current) return;
      const contentType = response.headers?.['content-type'] || '';
      if (!contentType.toLowerCase().includes('application/vnd.apple.pkpass') || !response.data?.size) {
        throw new Error('No Apple Wallet pass was returned');
      }
      const blob = new Blob([response.data], { type: 'application/vnd.apple.pkpass' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'staff-id.pkpass';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success('Apple Wallet pass ready — open it to add the card.');
    } catch (error) {
      if (context === contextRef.current) failed('apple', error);
    } finally {
      if (context === contextRef.current) {
        operationRef.current = false;
        setBusy(null);
      }
    }
  };

  const handleGoogle = async () => {
    if (!google.available || operationRef.current) return;
    operationRef.current = true;
    const context = contextRef.current;
    setBusy('google');
    try {
      const response = await axios.get(`${API_BASE}${paths.google}`, { headers: authHeaders });
      if (context !== contextRef.current) return;
      const saveUrl = response.data?.save_url;
      const destination = new URL(saveUrl);
      if (destination.origin !== 'https://pay.google.com' || !/^\/gp\/v\/save\/[A-Za-z0-9_.-]+$/.test(destination.pathname)) {
        throw new Error('No Google Wallet save link was returned');
      }
      window.location.assign(saveUrl);
    } catch (error) {
      if (context === contextRef.current) failed('google', error);
    } finally {
      if (context === contextRef.current) {
        operationRef.current = false;
        setBusy(null);
      }
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid="staff-wallet-buttons">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
        <WalletCards className="h-4 w-4" />
        Add to phone Wallet
      </div>
      <p className="mt-1 text-xs text-slate-600">
        Uses this exact staff number and QR. Scanning the QR always shows your live CareOps status.
      </p>

      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        <div>
          <Button
            type="button"
            variant={apple.available ? 'default' : 'outline'}
            className="w-full justify-center gap-2 rounded-xl"
            onClick={handleApple}
            disabled={!apple.available || Boolean(busy)}
            data-testid="wallet-apple-button"
          >
            {busy === 'apple' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Apple className="h-4 w-4" />}
            Apple Wallet
          </Button>
          {!apple.available && (
            <p className="mt-1 text-[11px] leading-snug text-slate-500" data-testid="wallet-apple-status">
              {describeSetup(apple)}
            </p>
          )}
        </div>

        <div>
          <Button
            type="button"
            variant={google.available ? 'default' : 'outline'}
            className="w-full justify-center gap-2 rounded-xl"
            onClick={handleGoogle}
            disabled={!google.available || Boolean(busy)}
            data-testid="wallet-google-button"
          >
            {busy === 'google' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Smartphone className="h-4 w-4" />}
            Google Wallet
          </Button>
          {!google.available && (
            <p className="mt-1 text-[11px] leading-snug text-slate-500" data-testid="wallet-google-status">
              {describeSetup(google)}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
